import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link2,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Minus,
  ImagePlus,
  Code2,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Extends the default Image node with a `width` attribute so users can resize
// images (stored as inline CSS, which every email client respects).
const ResizableImage = Image.extend({
  addAttributes() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      width: {
        default: null,
        parseHTML: (el) => {
          const styleWidth = el.style?.width?.trim();
          if (styleWidth) return styleWidth;
          const attrWidth = el.getAttribute('width');
          return attrWidth || null;
        },
        renderHTML: (attrs) => {
          if (!attrs.width) return {};
          return { style: `width: ${attrs.width}; height: auto;` };
        },
      },
    };
  },
});

function normalizeWidthInput(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!v) return null;
  if (/^\d+(\.\d+)?$/.test(v)) return `${v}px`;
  if (/^\d+(\.\d+)?(px|%|em|rem|vw)$/i.test(v)) return v.toLowerCase();
  return null;
}

function emailTemplateAssetsBucket(): string {
  const fromEnv = import.meta.env.VITE_SUPABASE_EMAIL_TEMPLATE_ASSETS_BUCKET?.trim();
  return fromEnv || 'email-template-assets';
}

function uploadErrorMessage(err: unknown, bucket: string): string {
  const raw =
    err && typeof err === 'object' && 'message' in err
      ? String((err as { message: unknown }).message)
      : err instanceof Error
        ? err.message
        : 'Image upload failed.';
  if (/bucket not found/i.test(raw)) {
    return `Storage bucket "${bucket}" does not exist on this Supabase project. Apply the migration supabase/migrations/20250608000000_email_template_assets_bucket.sql (e.g. supabase db push), or create the bucket in the Supabase Dashboard with the same name and storage policies.`;
  }
  return raw;
}

type Props = {
  value: string;
  onChange: (html: string) => void;
  showHtmlSource: boolean;
  onToggleSource: () => void;
  readOnly?: boolean;
};

export function EmailTemplateEditor({
  value,
  onChange,
  showHtmlSource,
  onToggleSource,
  readOnly = false,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      ResizableImage.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          style: 'max-width:100%;height:auto;display:block;',
        },
      }),
      Placeholder.configure({
        placeholder: 'Write the email body… Use toolbar for formatting, images, and links.',
      }),
    ],
    content: value || '',
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          'email-template-prose max-w-none min-h-[220px] px-3 py-2 focus:outline-none border border-gray-300 rounded-md bg-white',
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (readOnly) return;
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor || showHtmlSource) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor, showHtmlSource]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const setImageWidth = useCallback(
    (width: string | null) => {
      if (!editor) return;
      editor.chain().focus().updateAttributes('image', { width }).run();
    },
    [editor],
  );

  const promptImageWidth = useCallback(() => {
    if (!editor) return;
    const current = (editor.getAttributes('image').width as string | undefined) || '';
    const input = window.prompt(
      'Image width (e.g. 50%, 320px). Leave empty to reset.',
      current,
    );
    if (input === null) return;
    if (input.trim() === '') {
      setImageWidth(null);
      return;
    }
    const normalized = normalizeWidthInput(input);
    if (!normalized) {
      window.alert('Invalid width. Use values like 50%, 320px, or 20em.');
      return;
    }
    setImageWidth(normalized);
  }, [editor, setImageWidth]);

  const onImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor) return;
    if (!file.type.startsWith('image/')) {
      window.alert('Please choose an image file.');
      return;
    }
    setUploading(true);
    const bucket = emailTemplateAssetsBucket();
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const safeExt = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) ? ext : 'png';
      const path = `uploads/${crypto.randomUUID()}.${safeExt}`;
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      const src = pub.publicUrl;
      editor.chain().focus().setImage({ src, alt: file.name.replace(/\.[^.]+$/, '') }).run();
    } catch (err) {
      console.error(err);
      window.alert(uploadErrorMessage(err, bucket));
    } finally {
      setUploading(false);
    }
  };

  if (showHtmlSource && !readOnly) {
    return (
      <div className="space-y-2">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onToggleSource}
            className="text-sm text-action font-medium hover:underline"
          >
            Visual editor
          </button>
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={14}
          className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
          spellCheck={false}
        />
      </div>
    );
  }

  if (!editor) {
    return <div className="min-h-[220px] rounded-md border border-gray-200 bg-gray-50 animate-pulse" />;
  }

  return (
    <div className="space-y-2">
      {!readOnly ? (
        <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        className="hidden"
        onChange={onImageFile}
      />
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-gray-200 bg-gray-50 p-1">
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-6 bg-gray-200 mx-0.5" />
        <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Link">
          <Link2 className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-6 bg-gray-200 mx-0.5" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-6 bg-gray-200 mx-0.5" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
        >
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          active={false}
          title="Horizontal rule"
        >
          <Minus className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-6 bg-gray-200 mx-0.5" />
        <ToolbarBtn
          onClick={() => fileInputRef.current?.click()}
          active={false}
          title="Insert image"
          disabled={uploading}
        >
          <ImagePlus className="w-4 h-4" />
        </ToolbarBtn>
        {editor.isActive('image') && (
          <>
            <span className="w-px h-6 bg-gray-200 mx-0.5" />
            <span className="text-[11px] font-medium text-slate-500 pl-1 pr-0.5">
              Image width:
            </span>
            {(['25%', '50%', '75%', '100%'] as const).map((preset) => {
              const current = editor.getAttributes('image').width as string | undefined;
              const isActive = current === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  title={`Resize image to ${preset}`}
                  onClick={() => setImageWidth(preset)}
                  className={`rounded px-1.5 py-1 text-[11px] font-medium transition-colors ${
                    isActive
                      ? 'bg-action-surface text-action shadow-sm'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900'
                  }`}
                >
                  {preset}
                </button>
              );
            })}
            <button
              type="button"
              title="Custom width"
              onClick={promptImageWidth}
              className="rounded px-1.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-white hover:text-slate-900"
            >
              Custom…
            </button>
            <button
              type="button"
              title="Reset image width"
              onClick={() => setImageWidth(null)}
              className="rounded px-1.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-white hover:text-slate-900"
            >
              Reset
            </button>
          </>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onToggleSource}
          className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-white border border-transparent hover:border-gray-200"
          title="Edit raw HTML"
        >
          <Code2 className="w-4 h-4" /> HTML
        </button>
      </div>
        </>
      ) : null}
      {uploading && <p className="text-xs text-slate-500">Uploading image…</p>}
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarBtn({
  children,
  onClick,
  active,
  title,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  active: boolean;
  title: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`rounded p-1.5 transition-colors ${
        active ? 'bg-action-surface text-action shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'
      } disabled:opacity-50`}
    >
      {children}
    </button>
  );
}
